import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Flex, Text, HStack, VStack, Spinner, IconButton, SimpleGrid, Button,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
} from '@chakra-ui/react';
import { FiChevronLeft, FiChevronRight, FiExternalLink, FiFileText } from 'react-icons/fi';
import { getStudentLogs, listStudentStatuses } from 'services/qrService';
import { getSurveyResponse } from 'services/surveyService';
import ModalSurveyResponse from 'components/Modal/ModalSurveyResponse';

const openProfile = (id) => {
  if (!id) return;
  window.open(`${window.location.origin}/#/admin/usuario/${id}`, '_blank');
};

// Historial de un alumno en formato calendario — reutilizado por "Por Curso"
// (AttendanceView), "Historial de Accesos" (AccessLogs) y el tab "Accesos QR"
// del Perfil del alumno, para que las tres vistas compartan la misma forma
// de explorar el historial individual.

// Día calendario en hora LOCAL (no UTC) — new Date().toISOString().slice(0,10)
// da el día UTC, que entre ~20:00 y 23:59 hora Chile ya es el día siguiente.
const localDateStr = (d = new Date()) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const todayStr = () => localDateStr();

const addDays = (dateStr, n) => {
  const d = new Date(dateStr + 'T12:00:00');
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

const isWeekend = (dateStr) => {
  const dow = new Date(dateStr + 'T12:00:00').getDay();
  return dow === 0 || dow === 6;
};

const isFuture = (dateStr) => dateStr > todayStr();

const fmtTime = (dateStr) =>
  dateStr ? new Date(dateStr).toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' }) : null;

const getMonthStartOf = (dateStr) => dateStr.slice(0, 8) + '01';

const getMonthDays = (monthStart) => {
  const d = new Date(monthStart + 'T12:00:00');
  const count = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  return Array.from({ length: count }, (_, i) => {
    const day = new Date(d.getFullYear(), d.getMonth(), i + 1);
    return day.toISOString().slice(0, 10);
  });
};

const getMonthFirstDow = (monthStart) => {
  const dow = new Date(monthStart + 'T12:00:00').getDay();
  return dow === 0 ? 6 : dow - 1; // 0=Lun … 6=Dom
};

const fmtMonthYear = (monthStart) =>
  new Date(monthStart + 'T12:00:00').toLocaleDateString('es-CL', { month: 'long', year: 'numeric' });

const getLateThreshold = () => localStorage.getItem('attendance-late-threshold') || '08:30';

const JUSTIFY_TYPE_LABEL = {
  medical_leave:    'Licencia médica',
  suspended:        'Suspendido',
  pedagogical_exit: 'Salida pedagógica',
  sick_exit:        'Salida por enfermedad',
};

// pedagogical/pickup/sick son los 3 motivos vigentes; el resto queda para
// mostrar bien las salidas anticipadas ya registradas antes de este cambio.
const EXIT_REASON_LABELS = {
  pedagogical:   'Salida pedagógica',
  pickup:        'Retiro',
  sick:          'Enfermedad',
  medical:       'Cita médica',
  parent_pickup: 'Retiro apoderado',
  emergency:     'Emergencia',
  authorized:    'Act. autorizada',
  discipline:    'Disciplina',
  other:         'Otro',
};

const findActiveStatus = (statuses, rut, date) =>
  (statuses || []).find(s => {
    if (s.rut !== rut || !s.active) return false;
    const startOk = !s.starts_at || s.starts_at.slice(0, 10) <= date;
    const endOk = !s.ends_at || s.ends_at.slice(0, 10) >= date;
    return startOk && endOk;
  });

// Deriva entrada/salida del día a partir de logs ya filtrados a un solo alumno
const dayStatusFromLogs = (dayLogs) => {
  if (!dayLogs || !dayLogs.length) return null;
  const sorted = [...dayLogs].sort((a, b) => new Date(a.scanned_at) - new Date(b.scanned_at));
  const entryLog = sorted.find(l => l.access_type === 'entry');
  const exitLogs = sorted.filter(l => l.access_type === 'exit');
  const exitLog = exitLogs[exitLogs.length - 1];
  const earlyLog = exitLog && exitLog.early_exit ? exitLog : null;
  let status = null;
  if (entryLog && earlyLog) status = 'early_exit';
  else if (exitLog) status = 'exited';
  else if (entryLog) status = 'present';
  return { status, entryLog, exitLog, earlyLog };
};

// ─── Celda de día del calendario ──────────────────────────────────────────

const HistoryDayCell = ({ date, dayLogs, justification, lateThreshold }) => {
  const weekend = isWeekend(date);
  const future = isFuture(date);
  const isToday = date === todayStr();
  const dayNum = new Date(date + 'T12:00:00').getDate();

  let bg = 'transparent';
  let color = 'ink.400';
  let title = '';

  if (!weekend && !future) {
    if (justification) {
      bg = '#E2ECFB'; color = '#2A66B4';
      title = `Justificado · ${JUSTIFY_TYPE_LABEL[justification.type] ?? ''}`;
    } else {
      const ds = dayStatusFromLogs(dayLogs);
      if (!ds || !ds.status) {
        bg = '#F3F4F6'; color = '#9CA3AF'; title = 'Ausente';
      } else {
        const late = ds.entryLog && fmtTime(ds.entryLog.scanned_at) > lateThreshold;
        if (ds.status === 'early_exit') { bg = '#FEE2E2'; color = '#DC2626'; }
        else if (late) { bg = '#FEF3C7'; color = '#92400E'; }
        else if (ds.status === 'present') { bg = '#DCF1E6'; color = '#1E7A4E'; }
        else { bg = '#FCE7CC'; color = '#B5631A'; } // exited normal

        const parts = [];
        if (ds.entryLog) parts.push(`Entrada ${fmtTime(ds.entryLog.scanned_at)}${late ? ' (tarde)' : ''}`);
        if (ds.exitLog) {
          const reason = ds.earlyLog?.exit_reason ? ` — ${EXIT_REASON_LABELS[ds.earlyLog.exit_reason] ?? ds.earlyLog.exit_reason}` : '';
          parts.push(`Salida ${fmtTime(ds.exitLog.scanned_at)}${ds.earlyLog ? ` (anticipada${reason})` : ''}`);
        }
        title = parts.join(' · ');
      }
    }
  }

  return (
    <Flex
      minH="38px" borderRadius="9px" align="center" justify="center"
      bg={weekend || future ? 'transparent' : bg}
      border="1.5px solid"
      borderColor={isToday ? 'coral.500' : 'transparent'}
      opacity={weekend ? 0.3 : future ? 0.35 : 1}
      title={title || undefined}
      cursor={title ? 'default' : undefined}
    >
      <Text fontSize="11px" fontWeight={isToday ? '800' : '500'} color={weekend || future ? 'ink.400' : color}>
        {dayNum}
      </Text>
    </Flex>
  );
};

// ─── Contenido del historial (calendario + resumen + lista) ──────────────
// Sin envoltorio de Modal — se puede usar dentro de un modal o embebido
// directamente en una página (ej. tab "Accesos QR" del Perfil del alumno).

const StudentAccessHistory = ({ student }) => {
  const [allLogs, setAllLogs] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [calMonth, setCalMonth] = useState(() => getMonthStartOf(todayStr()));
  const lateThreshold = getLateThreshold();

  // "Ver ficha" de una salida anticipada con ficha firmada (Retiro/Salida
  // Pedagógica/Salida por Enfermedad) — mismo modal con cabecera/logo y PDF
  // que ya se usa en Perfil > Fichas y en el Scanner, reusado tal cual.
  const [isFichaOpen, setIsFichaOpen] = useState(false);
  const [fichaData, setFichaData] = useState(null);
  const handleOpenFicha = (responseId) => {
    setFichaData(null);
    setIsFichaOpen(true);
    getSurveyResponse(responseId)
      .then(setFichaData)
      .catch(() => setFichaData({ error: 'No se pudieron cargar los datos de la ficha' }));
  };

  useEffect(() => {
    if (!student?.username) return;
    setCalMonth(getMonthStartOf(todayStr()));
    setLoading(true);
    Promise.all([
      getStudentLogs(student.username).then(res => res?.data ?? res ?? []).catch(() => []),
      listStudentStatuses().then(res => res?.data ?? []).catch(() => []),
    ]).then(([logsRes, statusesRes]) => {
      setAllLogs(logsRes);
      setStatuses(statusesRes);
    }).finally(() => setLoading(false));
  }, [student?.username]);

  const logsByDate = useMemo(() => {
    const map = {};
    allLogs.forEach(l => {
      const d = l.scanned_at ? localDateStr(new Date(l.scanned_at)) : '';
      if (!map[d]) map[d] = [];
      map[d].push(l);
    });
    return map;
  }, [allLogs]);

  const monthDays = useMemo(() => getMonthDays(calMonth), [calMonth]);
  const firstDow = getMonthFirstDow(calMonth);
  const calendarSlots = [...Array(firstDow).fill(null), ...monthDays];

  const recentDays = useMemo(() => {
    const dates = Object.keys(logsByDate).sort((a, b) => b.localeCompare(a)).slice(0, 8);
    return dates.map(date => ({ date, ...dayStatusFromLogs(logsByDate[date]) }));
  }, [logsByDate]);

  // % asistencia de los últimos 30 días hábiles (excluye fines de semana,
  // días futuros y días con ausencia justificada)
  const attendance30d = useMemo(() => {
    if (!student?.username) return null;
    const today = todayStr();
    const start = addDays(today, -29);
    let present = 0;
    let total = 0;
    for (let d = start; d <= today; d = addDays(d, 1)) {
      if (isWeekend(d) || isFuture(d)) continue;
      if (findActiveStatus(statuses, student.username, d)) continue; // justificado: no cuenta
      total++;
      const ds = dayStatusFromLogs(logsByDate[d]);
      if (ds?.status) present++;
    }
    return { present, total, pct: total > 0 ? Math.round((present / total) * 100) : null };
  }, [logsByDate, statuses, student]);

  const navMonth = (delta) => setCalMonth(m => {
    const nd = new Date(m + 'T12:00:00');
    nd.setMonth(nd.getMonth() + delta);
    return nd.toISOString().slice(0, 8) + '01';
  });

  const DOW = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];

  if (loading) {
    return <VStack py={10}><Spinner color="coral.500" /></VStack>;
  }

  return (
    <Box fontFamily="jakarta">
      {/* % asistencia últimos 30 días */}
      {attendance30d?.pct !== null && (
        <Flex
          align="center" justify="space-between" mb="14px"
          bg={attendance30d.pct >= 80 ? 'mint.bg' : attendance30d.pct >= 60 ? 'butter.bg' : 'rose.bg'}
          borderRadius="14px" p={3}
        >
          <Box>
            <Text fontSize="11px" fontWeight="800" textTransform="uppercase" letterSpacing=".05em"
              color={attendance30d.pct >= 80 ? 'mint.ink' : attendance30d.pct >= 60 ? 'butter.ink' : 'rose.ink'}>
              Asistencia últimos 30 días
            </Text>
            <Text fontSize="12px" fontWeight="600" color="ink.400" mt="2px">
              {attendance30d.present} de {attendance30d.total} días hábiles
            </Text>
          </Box>
          <Text fontSize="26px" fontWeight="800" letterSpacing="-0.03em"
            color={attendance30d.pct >= 80 ? 'mint.ink' : attendance30d.pct >= 60 ? 'butter.ink' : 'rose.ink'}>
            {attendance30d.pct}%
          </Text>
        </Flex>
      )}

      <Flex align="center" justify="space-between" mb="10px">
        <IconButton icon={<FiChevronLeft />} size="xs" borderRadius="8px"
          variant="ghost" onClick={() => navMonth(-1)} aria-label="Mes anterior" />
        <Text fontSize="13px" fontWeight="700" color="ink.900" textTransform="capitalize">
          {fmtMonthYear(calMonth)}
        </Text>
        <IconButton icon={<FiChevronRight />} size="xs" borderRadius="8px"
          variant="ghost" onClick={() => navMonth(1)} aria-label="Mes siguiente" />
      </Flex>

      <SimpleGrid columns={7} gap="3px" mb="3px">
        {DOW.map((d, i) => (
          <Box key={i} textAlign="center">
            <Text fontSize="10px" fontWeight="700" color="ink.400">{d}</Text>
          </Box>
        ))}
      </SimpleGrid>
      <SimpleGrid columns={7} gap="3px" mb="14px">
        {calendarSlots.map((date, i) => date ? (
          <HistoryDayCell
            key={date} date={date} dayLogs={logsByDate[date]}
            justification={findActiveStatus(statuses, student.username, date)}
            lateThreshold={lateThreshold}
          />
        ) : <Box key={`e-${i}`} minH="38px" />)}
      </SimpleGrid>

      <HStack spacing="10px" flexWrap="wrap" mb="14px">
        {[
          { label: 'Presente', bg: '#DCF1E6', color: '#1E7A4E' },
          { label: 'Tardanza', bg: '#FEF3C7', color: '#92400E' },
          { label: 'Salida', bg: '#FCE7CC', color: '#B5631A' },
          { label: 'Salida anticipada', bg: '#FEE2E2', color: '#DC2626' },
          { label: 'Justificado', bg: '#E2ECFB', color: '#2A66B4' },
          { label: 'Ausente', bg: '#F3F4F6', color: '#9CA3AF' },
        ].map(it => (
          <Flex key={it.label} align="center" gap="5px">
            <Box w="11px" h="11px" borderRadius="3px" bg={it.bg} />
            <Text fontSize="10.5px" color="ink.400" fontWeight="600">{it.label}</Text>
          </Flex>
        ))}
      </HStack>

      <Text fontSize="12px" fontWeight="800" color="ink.700" mb="8px"
        textTransform="uppercase" letterSpacing=".04em">
        Últimos días con movimiento
      </Text>
      {recentDays.length === 0 ? (
        <Text fontSize="13px" color="ink.400" py={4} textAlign="center">Sin registros</Text>
      ) : (
        <VStack spacing={0} align="stretch">
          {recentDays.map((d, i) => {
            const late = d.entryLog && fmtTime(d.entryLog.scanned_at) > lateThreshold;
            const location = d.entryLog?.location ?? d.exitLog?.location;
            return (
              <Flex key={d.date} justify="space-between" align="flex-start" py="10px"
                borderTop={i === 0 ? 'none' : '1px solid'} borderColor="cream.line">
                <Box>
                  <Text fontSize="13px" fontWeight="700" color="ink.900" textTransform="capitalize">
                    {new Date(d.date + 'T12:00:00').toLocaleDateString('es-CL', { weekday: 'short', day: 'numeric', month: 'short' })}
                  </Text>
                  {location && <Text fontSize="11.5px" color="ink.400" mt="1px">{location}</Text>}
                </Box>
                <VStack align="flex-end" spacing="4px">
                  <HStack spacing="6px" flexWrap="wrap" justify="flex-end">
                    {d.entryLog && (
                      <Flex px="8px" py="3px" borderRadius="999px"
                        bg={late ? '#FEF3C7' : '#DCF1E6'} color={late ? '#92400E' : '#1E7A4E'}
                        fontSize="11px" fontWeight="700" whiteSpace="nowrap">
                        Entrada {fmtTime(d.entryLog.scanned_at)}{late ? ' · tarde' : ''}
                      </Flex>
                    )}
                    {d.exitLog && (
                      <Flex px="8px" py="3px" borderRadius="999px"
                        bg={d.earlyLog ? '#FEE2E2' : '#FCE7CC'} color={d.earlyLog ? '#DC2626' : '#B5631A'}
                        fontSize="11px" fontWeight="700" whiteSpace="nowrap">
                        Salida {fmtTime(d.exitLog.scanned_at)}{d.earlyLog ? ' · anticipada' : ''}
                      </Flex>
                    )}
                  </HStack>
                  {d.earlyLog?.exit_reason && (
                    <Text fontSize="10.5px" color="#B91C1C" fontWeight="600">
                      {EXIT_REASON_LABELS[d.earlyLog.exit_reason] ?? d.earlyLog.exit_reason}
                    </Text>
                  )}
                  {d.earlyLog?.survey_response_id && (
                    <Button
                      size="xs" variant="link" color="coral.600" fontWeight="700"
                      leftIcon={<FiFileText size="11px" />}
                      onClick={() => handleOpenFicha(d.earlyLog.survey_response_id)}
                    >
                      Ver ficha
                    </Button>
                  )}
                </VStack>
              </Flex>
            );
          })}
        </VStack>
      )}

      <ModalSurveyResponse
        isOpen={isFichaOpen}
        onClose={() => setIsFichaOpen(false)}
        customer={student}
        data={fichaData}
      />
    </Box>
  );
};

// ─── Modal (Por Curso / Historial de Accesos) ─────────────────────────────

const StudentHistoryModal = ({ isOpen, onClose, student }) => {
  if (!student) return null;
  const name = [student.first_name, student.last_name].filter(Boolean).join(' ') || student.username;

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="md" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent mx={3} fontFamily="jakarta" borderRadius="18px">
        <ModalHeader fontSize="16px" fontWeight="800" color="ink.900" pb="4px">
          {name}
          <Text fontSize="12px" color="ink.400" fontWeight="500" mt="2px">{student.username}</Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <StudentAccessHistory student={student} />
        </ModalBody>
        <ModalFooter gap={2}>
          {student.id && (
            <Button
              size="sm" variant="outline" borderRadius="9px" fontWeight="700"
              borderColor="cream.line2" color="ink.700"
              _hover={{ borderColor: 'coral.500', color: 'coral.500' }}
              leftIcon={<FiExternalLink />}
              onClick={() => openProfile(student.id)}
              mr="auto"
            >
              Ver perfil
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={onClose}>Cerrar</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export { JUSTIFY_TYPE_LABEL, findActiveStatus, getLateThreshold, StudentAccessHistory };
export default StudentHistoryModal;
